import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Check, Crown, Loader2, Sparkles, Infinity } from 'lucide-react';
import { PollarBrandMini } from '@/components/PollarBrand';
import { trackPageView, trackClick, trackEvent, EventTypes } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Subscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      localStorage.setItem('facilitator', JSON.stringify(response.data));
    } catch (error) {
      console.error('Failed to load profile');
      navigate('/login');
    }
  }, [navigate]);

  // Use a ref to keep track of whether we are currently polling to avoid duplicates
  // and to allow the function to reference itself safely
  const pollPaymentStatus = useCallback(async (checkoutSessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please refresh the page.');
      setCheckingStatus(false);
      return;
    }

    setCheckingStatus(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/payment/checkout-status/${checkoutSessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        // Track successful payment
        trackEvent(EventTypes.SUCCESSFUL_PAYMENT, window.location.href);
        
        // Google Ads conversion tracking for subscription
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-17773299756/ttwMCMyrsPkbEKyQ_JpC'
          });
        }
        
        toast.success('🎉 Payment successful! You are now a Premium member!');
        await fetchProfile(); // Refresh profile to show premium status
        setCheckingStatus(false);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingStatus(false);
        return;
      }

      // Continue polling
      // We need to reference the function itself. 
      // Since we are in a useCallback, we can't reference 'pollPaymentStatus' directly if it's not defined yet.
      // But we can use a timeout that calls a function that *will* be defined.
      // However, correct way to handle recursion in useCallback is slightly complex.
      // Simpler: use a regular function outside or inside useEffect, but we want to call it.
      
      // Let's use a self-referencing timeout pattern that doesn't depend on the variable name in the closure immediately
      setTimeout(() => pollPaymentStatus(checkoutSessionId, attempts + 1), pollInterval);

    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error verifying payment. Please contact support if payment was successful.');
      setCheckingStatus(false);
    }
  }, [fetchProfile]); // We can add pollPaymentStatus to dependency in useEffect, but here it's tricky.

  // Actually, the previous linter error was about "access variable before declaration".
  // If I use a function declaration, it's hoisted.
  
  /* 
     Recalibrating strategy:
     I will define pollPaymentStatus as a standard function declaration.
     This allows hoisting and self-reference.
     I will wrap it in useCallback? No, function declarations can't be wrapped in useCallback directly.
     I will just define it inside the component. It will be recreated on every render, which is fine for this component.
     Or I can put it inside useEffect.
  */
  
  // Strategy: Move polling logic entirely inside useEffect to avoid complexity
  
  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login?signup=true');
      return;
    }
    
    trackPageView('subscription');
    fetchProfile();
  }, [fetchProfile, navigate]);

  useEffect(() => {
    if (!sessionId) return;

    let isActive = true;
    let timeoutId;

    const poll = async (attempts = 0) => {
      if (!isActive) return;
      
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        toast.error('Payment verification timed out. Please refresh the page.');
        setCheckingStatus(false);
        return;
      }

      setCheckingStatus(true);

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/payment/checkout-status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!isActive) return;

        if (response.data.payment_status === 'paid') {
          trackEvent(EventTypes.SUCCESSFUL_PAYMENT, window.location.href);
          
          // Google Ads conversion tracking for subscription
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'conversion', {
              'send_to': 'AW-17773299756/ttwMCMyrsPkbEKyQ_JpC'
            });
          }
          
          toast.success('🎉 Payment successful! You are now a Premium member!');
          await fetchProfile();
          setCheckingStatus(false);
          return;
        } else if (response.data.status === 'expired') {
          toast.error('Payment session expired. Please try again.');
          setCheckingStatus(false);
          return;
        }

        timeoutId = setTimeout(() => poll(attempts + 1), pollInterval);
      } catch (error) {
        if (!isActive) return;
        console.error('Error checking payment status:', error);
        toast.error('Error verifying payment. Please contact support if payment was successful.');
        setCheckingStatus(false);
      }
    };

    poll();

    return () => {
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [sessionId, fetchProfile]);


  const handleUpgrade = async (planType = 'monthly') => {
    setLoading(true);
    
    // Track upgrade button click
    trackClick(`Upgrade ${planType}`, EventTypes.CLICK_UPGRADE);

    try {
      const token = localStorage.getItem('token');
      const originUrl = window.location.origin;

      const response = await axios.post(
        `${API}/payment/create-checkout`,
        null,
        {
          params: { origin_url: originUrl, plan_type: planType },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start payment process');
      setLoading(false);
    }
  };

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-4 max-w-4xl">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
            <PollarBrandMini />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {checkingStatus && (
          <Card className="mb-6 border-indigo-200 bg-indigo-50 max-w-4xl mx-auto">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
                <span className="text-indigo-900 font-medium">Verifying your payment...</span>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Stop Paying for Raw Data. Pay for Answers.
          </h1>
          <p className="text-gray-600 text-lg">
            Most tools just collect feedback and leave you to sort the mess. Pollar organizes the noise into insights instantly—standard in every plan.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Free Plan */}
          <Card className={`border-2 ${!profile.is_paid ? 'border-indigo-500 shadow-lg' : 'border-gray-200'}`}>
            <CardHeader>
              <div className="flex items-center justify-between mb-2">
                <CardTitle className="text-2xl">Free</CardTitle>
                {!profile.is_paid && <Badge>Current Plan</Badge>}
              </div>
              <div className="text-3xl font-bold">$0<span className="text-lg font-normal text-gray-600">/month</span></div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Up to <strong>10 sessions</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Max <strong>10 participants</strong> per session</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Basic time limit control</span>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="text-xl">✗</span>
                  <span>Session search</span>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="text-xl">✗</span>
                  <span>Idea ranking results</span>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="text-xl">✗</span>
                  <span>End date/time scheduling</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Monthly Premium Plan */}
          <Card className={`border-2 ${profile.is_paid && profile.subscription_status !== 'lifetime' ? 'border-purple-500 shadow-lg' : 'border-purple-200'} relative overflow-hidden`}>
            {profile.is_paid && profile.subscription_status !== 'lifetime' && (
              <div className="absolute top-0 right-0 bg-gradient-to-br from-purple-500 to-pink-500 text-white px-3 py-1 text-xs font-bold rounded-bl-lg">
                CURRENT
              </div>
            )}
            <CardHeader>
              <div className="flex items-center justify-between mb-2">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Crown className="h-6 w-6 text-purple-600" />
                  Monthly
                </CardTitle>
              </div>
              <div className="text-3xl font-bold text-purple-600">
                <span className="text-xl text-gray-400 line-through decoration-red-500/50 decoration-2 mr-2">$28</span>
                $7.99<span className="text-lg font-normal text-gray-600">/month</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Unlimited sessions</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span>Up to <strong>100 participants</strong> per session</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span>Advanced time controls</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Full session search</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Complete idea ranking access</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span><strong>End date/time scheduling</strong></span>
                </li>
              </ul>
              
              {!profile.is_paid && (
                <Button
                  onClick={() => handleUpgrade('monthly')}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-6 text-lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Crown className="mr-2 h-5 w-5" />
                      Subscribe Monthly
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Lifetime Access Plan */}
          <Card className={`border-2 ${profile.subscription_status === 'lifetime' ? 'border-amber-500 shadow-lg' : 'border-amber-200'} relative overflow-hidden bg-gradient-to-b from-amber-50 to-white`}>
            <div className="absolute top-0 right-0 bg-gradient-to-br from-amber-400 to-orange-500 text-white px-3 py-1 text-xs font-bold rounded-bl-lg flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> BEST VALUE
            </div>
            <CardHeader>
              <div className="flex items-center justify-between mb-2">
                <CardTitle className="text-2xl flex items-center gap-2 text-amber-800">
                  <Infinity className="h-6 w-6 text-amber-600" />
                  Lifetime
                </CardTitle>
                {profile.subscription_status === 'lifetime' && <Badge variant="secondary" className="bg-amber-100 text-amber-800">Current Plan</Badge>}
              </div>
              <div className="text-3xl font-bold text-amber-600">
                $288<span className="text-lg font-normal text-gray-600"> one-time</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-gray-600 mb-2">Pay once, own it forever. No monthly fees.</div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span><strong>All Premium features included</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span><strong>Future updates included</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span>No recurring charges ever</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span>Priority support</span>
                </li>
              </ul>
              
              {!profile.is_paid && (
                <Button
                  onClick={() => handleUpgrade('lifetime')}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-6 text-lg mt-8"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-5 w-5" />
                      Get Lifetime Access
                    </>
                  )}
                </Button>
              )}

              {profile.is_paid && profile.subscription_status === 'lifetime' && (
                <div className="bg-amber-100 border border-amber-200 rounded-lg p-4 mt-8">
                  <p className="text-sm text-amber-900 text-center font-medium">
                    🎉 You have lifetime access!
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* FAQ or Additional Info */}
        <Card className="mt-12 border-gray-200 max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle className="text-lg">Why upgrade to Premium?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-gray-700">
            <p>
              <strong>Unlimited Sessions:</strong> Create as many ideation sessions as you need without restrictions.
            </p>
            <p>
              <strong>Larger Groups:</strong> Accommodate up to 100 participants for bigger brainstorming events.
            </p>
            <p>
              <strong>Better Insights:</strong> Access complete voting results and ranking data to make informed decisions.
            </p>
            <p>
              <strong>Flexible Scheduling:</strong> Set specific end dates and times instead of just countdown timers.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Subscription;